import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { SessionsFilterBarProps } from '../../../types/dashboardV2';
import { FilterBar as GenericFilterBar } from '../../../components/common/FilterBar/FilterBar';
import { DateRangePicker } from '../../../components/common/DateRangePicker/DateRangePicker';
import { StatusFilterDropdown } from '../../../components/common/StatusFilterDropdown/StatusFilterDropdown';
import { StationFilterDropdown } from '../../../components/common/StationFilterDropdown/StationFilterDropdown';
import { SESSION_STATUS_OPTIONS } from '../../../constants/filterOptions';
import { SessionStatus } from '../../../shared/enums/status';
import { useStationOptionsQuery } from '../../../hooks/useDashboardQueries';

export const SessionsFilterBar = memo(function SessionsFilterBar({
  filters,
  onFilterChange,
  onRefresh,
}: SessionsFilterBarProps) {
  const { t } = useTranslation();

  const handleStatusChange = useCallback(
    (status: SessionStatus | '') => onFilterChange({ status }),
    [onFilterChange],
  );

  const handleSearchChange = useCallback(
    (search: string) => onFilterChange({ search }),
    [onFilterChange],
  );

  const handleDateRangeChange = useCallback(
    (dateRange: string) => onFilterChange({ dateRange }),
    [onFilterChange],
  );

  const handleStationsChange = useCallback(
    (stationIds: string[]) => onFilterChange({ stationIds }),
    [onFilterChange],
  );

  const { data: stationOptions = [], isLoading: stationsLoading } = useStationOptionsQuery();

  return (
    <GenericFilterBar
      search={{
        value: filters.search,
        onChange: handleSearchChange,
        placeholder: t('dashboard.sessions.searchPlaceholder'),
        ariaLabel: t('dashboard.sessions.searchPlaceholder'),
        name: 'session-search',
      }}
      controls={
        <>
          <StatusFilterDropdown
            value={filters.status}
            options={SESSION_STATUS_OPTIONS}
            onChange={handleStatusChange}
            ariaLabel={t('dashboard.filters.aria.filterSessionsStatus')}
          />
          <StationFilterDropdown
            value={filters.stationIds}
            options={stationOptions}
            onChange={handleStationsChange}
            ariaLabel={t('dashboard.filters.aria.filterSessionsStation')}
            isLoading={stationsLoading}
          />
          <DateRangePicker value={filters.dateRange} onChange={handleDateRangeChange} />
        </>
      }
      privacyScope="sessions"
      onRefresh={onRefresh}
      refreshAriaLabel={t('dashboard.filters.aria.refreshSessions')}
    />
  );
});

export default SessionsFilterBar;
